import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Plus } from "lucide-react";
import { FORMULARIO } from "../utils/const/formulario-const";
import Switch from "./switch";
import { obtenerListaQuienRecibeEntrega } from "../api/api-quien-recibe-entrega";
import { obtenerListaEstadoPago } from "../api/api-estado-pago";

export default function DynamicFormularioBuilder({ fields, setFields }) {
    const { t } = useTranslation();
    const [listaQuienRecibeEntrega, setListaQuienRecibeEntrega] = useState([]);
    const [listaEstadoDePago, setListaEstadoDePago] = useState([]);

    useEffect(() => {
        iniciar();
    }, []);

    const iniciar = async () => {
        try {
            const quienRecibeEntrega = await obtenerListaQuienRecibeEntrega({ habilitado: 1 }, true, null);
            setListaQuienRecibeEntrega(quienRecibeEntrega);
            const estadoDePago = await obtenerListaEstadoPago({ habilitado: 1 }, true, null);
            setListaEstadoDePago(estadoDePago);
        } catch (err) {
            console.log('iniciar err');
            console.log(err);
        }
    };

    const inputTypeOptions = [
        { value: FORMULARIO.FIRMA, label: t("label_firma") },
        { value: FORMULARIO.FOTOGRAFIA, label: t("label_fotografia") },
        { value: FORMULARIO.ESTADO_DE_PAGO, label: t("label_estado_de_pago") },
        { value: FORMULARIO.QUIEN_RECIBE_ENTREGA, label: t("label_quien_recibe_entrega") },
        { value: FORMULARIO.NOTA, label: t("label_nota") },
        { value: FORMULARIO.FECHA, label: t("label_fecha") },
        { value: FORMULARIO.HORA, label: t("label_hora") },
    ];

    const [selectedType, setSelectedType] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // useEffect(() => {
    // console.log("selectedType");
    // console.log(selectedType);
    // if (selectedType !== null) { }
    // }, [selectedType]);



    const handleAddField = () => {
        if (!selectedType) return;
        // switch (selectedType.value) {
        //     case FORMULARIO.FIRMA:
        addNewField(selectedType);
        //         break;
        //     case FORMULARIO.FOTOGRAFIA:
        //         addNewField(FORMULARIO.FOTOGRAFIA);
        //         break;
        //     case FORMULARIO.ESTADO_DE_PAGO:
        //         addNewField(FORMULARIO.ESTADO_DE_PAGO);
        //         break;
        //     case FORMULARIO.QUIEN_RECIBE_ENTREGA:
        //         addNewField(FORMULARIO.QUIEN_RECIBE_ENTREGA);
        //         break;
        //     case FORMULARIO.NOTA:
        //         addNewField(FORMULARIO.NOTA);
        //         break;
        //     case FORMULARIO.FECHA:
        //         addNewField(FORMULARIO.FECHA);
        //         break;
        // }
    };

    const addNewField = (selectedType) => {
        const newField = {
            id: `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: selectedType.value,
            label: "",
            value: "",
            enabled: true,
            order: fields.length,
            estadoDePagoSelected: null,
            quienRecibeEntregaSelected: null,
            obligatorio: false,
        };
        setFields([...fields, newField]);
        setSelectedType(null);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setFields((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                const newFields = arrayMove(items, oldIndex, newIndex);
                // Update order property
                return newFields.map((field, index) => ({
                    ...field,
                    order: index,
                }));
            });
        }
    };

    const handleDeleteField = (id) => {
        setFields(fields.filter((field) => field.id !== id));
    };

    const handleToggleEnabled = (id) => {
        setFields(
            fields.map((field) =>
                field.id === id ? { ...field, enabled: !field.enabled } : field
            )
        );
    };

    const handleToggleObligatorio = (id) => {
        setFields(
            fields.map((field) =>
                field.id === id ? { ...field, obligatorio: !field.obligatorio } : field
            )
        );
    };

    const handleFieldChange = (id, key, value) => {
        // If key is not 'key' or 'label', return early
        if (key !== "key" && key !== "label") {
            return;
        }
        let filteredValue = value;

        if (key === "key") {
            // Convert to lowercase first, then filter out any characters that aren't a-z or _
            filteredValue = value.toLowerCase().replace(/[^a-z_]/g, "");
        }
        if (key === "label") {
            // Allow only numbers, alphabet letters (uppercase and lowercase), comma, dot, parentheses, and spaces
            filteredValue = value.replace(/[^a-zA-Z0-9,()._ ]/g, "");

            // Also update the 'key' field with the same value but applying key validation
            const keyValue = value.toLowerCase().replace(/[^a-z_]/g, "").replace(/\s+/g, "_");

            setFields(
                fields.map((field) =>
                    field.id === id
                        ? { ...field, label: filteredValue, key: keyValue }
                        : field
                )
            );
            return;
        }

        setFields(
            fields.map((field) =>
                field.id === id ? { ...field, [key]: filteredValue } : field
            )
        );
    };

    const handleSelectChange = (id, selectType, selectedValue) => {
        setFields(
            fields.map((field) =>
                field.id === id
                    ? { ...field, [selectType]: selectedValue }
                    : field
            )
        );
    };

    return (
        <div className="w-full">
            {/* Add Field Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("label_agregar_campo")}
                </label>
                <div className="flex gap-2">
                    <div className="flex-1">
                        <Select
                            options={inputTypeOptions}
                            value={selectedType}
                            onChange={setSelectedType}
                            placeholder={t("placeholder_seleccione_tipo")}
                            isSearchable={false}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleAddField}
                        disabled={!selectedType}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        {t("label_agregar")}
                    </button>
                </div>
            </div>

            {/* Fields List */}
            <div className="space-y-3">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={fields.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {fields.map((field) => {
                            // console.log("field");
                            // console.log(field);
                            return (
                                <SortableFieldItem
                                    key={field.id}
                                    field={field}
                                    onDelete={handleDeleteField}
                                    onToggleEnabled={handleToggleEnabled}
                                    onChangeField={handleFieldChange}
                                    onSelectChange={handleSelectChange}
                                    listaEstadoDePago={listaEstadoDePago}
                                    listaQuienRecibeEntrega={listaQuienRecibeEntrega}
                                    onToggleObligatorio={handleToggleObligatorio}
                                />
                            )
                        })}
                    </SortableContext>
                </DndContext>

                {fields.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        {t("label_no_hay_campos")}
                    </div>
                )}
            </div>
        </div>
    );
};



function SortableFieldItem({ field, onDelete, onToggleEnabled, onChangeField, onSelectChange, listaEstadoDePago, listaQuienRecibeEntrega, onToggleObligatorio }) {
    const { t } = useTranslation();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`bg-white border rounded-lg p-4 shadow-sm ${!field.enabled ? "opacity-60" : ""
                }`}
        >
            <div className="flex items-start gap-3">
                {/* Drag Handle */}
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing mt-2 text-gray-400 hover:text-gray-600"
                >
                    <GripVertical className="h-5 w-5" />
                </div>

                {/* Field Content */}
                <div className="flex-1 space-y-3">
                    {/* Label Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("label_key")} <span className="text-xs text-gray-500">({t("label_only_letters_and_underscore_allowed")})</span><span className="text-xs text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={field.key || ""}
                            onChange={(e) => onChangeField(field.id, "key", e.target.value)}
                            placeholder={t("placeholder_ingresa_key")}
                            disabled={!field.enabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            maxLength={64}
                        />
                        <p className="text-sm text-gray-500 flex justify-end">{field.key?.length}/64</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("label_etiqueta")}<span className="text-xs text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={field.label || ""}
                            onChange={(e) => onChangeField(field.id, "label", e.target.value)}
                            placeholder={t("placeholder_ingresa_etiqueta")}
                            disabled={!field.enabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            maxLength={128}
                        />
                        <p className="text-sm text-gray-500 flex justify-end">{field.label?.length}/128</p>
                    </div>

                    {/* Type Display */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("label_tipo")}
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
                            {field.type === FORMULARIO.FIRMA && (t("label_firma"))}
                            {field.type === FORMULARIO.FOTOGRAFIA && (t("label_fotografia"))}
                            {field.type === FORMULARIO.ESTADO_DE_PAGO && (t("label_estado_de_pago"))}
                            {field.type === FORMULARIO.QUIEN_RECIBE_ENTREGA && (t("label_quien_recibe_entrega"))}
                            {field.type === FORMULARIO.NOTA && (t("label_nota"))}
                            {field.type === FORMULARIO.FECHA && (t("label_fecha"))}
                            {field.type === FORMULARIO.HORA && (t("label_hora"))}
                        </div>
                    </div>

                    {/* Value Input */}
                    {field.type === FORMULARIO.ESTADO_DE_PAGO && (
                        <Select
                            options={listaEstadoDePago}
                            value={field.estadoDePagoSelected || null}
                            onChange={(e) => onSelectChange(field.id, "estadoDePagoSelected", e)}
                            placeholder={t("placeholder_seleccione_estado_de_pago")}
                            isSearchable={true}
                            isClearable={true}
                            isMulti={true}
                            className="react-select"
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 999 }),
                                menu: (base) => ({ ...base, zIndex: 999 }),
                            }}
                        />
                    )}
                    {field.type === FORMULARIO.QUIEN_RECIBE_ENTREGA && (
                        <Select
                            options={listaQuienRecibeEntrega}
                            value={field.quienRecibeEntregaSelected || null}
                            onChange={(e) => onSelectChange(field.id, "quienRecibeEntregaSelected", e)}
                            placeholder={t("placeholder_seleccione_quien_recibe_entrega")}
                            isSearchable={true}
                            isClearable={true}
                            isMulti={true}
                            className="react-select"
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 999 }),
                                menu: (base) => ({ ...base, zIndex: 999 }),
                            }}
                        />
                    )}

                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 mt-2">
                    <div className="flex items-center gap-3">
                        {/* Enable/Disable Switch */}
                        <Switch
                            checked={field.enabled}
                            onChange={() => onToggleEnabled(field.id)}
                            label={field.enabled ? t("label_habilitado") : t("label_deshabilitado")}
                        />

                        {/* Delete Button */}
                        <button
                            type="button"
                            onClick={() => onDelete(field.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title={t("label_eliminar")}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Obligatorio Switch */}
                        <Switch
                            checked={field.obligatorio}
                            onChange={() => onToggleObligatorio(field.id)}
                            label={field.obligatorio ? t("label_obligatorio") : t("label_no_obligatorio")}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

